import React from 'react';
import PropTypes from 'prop-types';
import { endOfMonth, endOfYear, subYears, parseISO } from 'date-fns';
import { reportFieldEngagement } from '../../../bootstrap';
import {
  isBefore,
  isAfter,
  parseMMDDYYYY,
  parseYYYYMMDD,
  formatToYYYYMMDD,
  formatToMMDDYYYY,
} from '../../../utils/dateUtils';

class NativeDatePicker extends React.Component {
  constructor(props) {
    super(props);
    const initialValue = this.formatToNative(props.value);

    this.state = {
      selectedDate: initialValue,
      lastReportedValue: '',
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      const nativeValue = this.formatToNative(this.props.value);
      this.setState({ selectedDate: nativeValue });
    }
  }

  formatToNative = (value) => {
    if (!value) return '';

    const parsed = parseMMDDYYYY(value);
    return parsed ? formatToYYYYMMDD(parsed) : '';
  };

  formatToApp = (value) => {
    if (!value) return '';

    const parsed = parseYYYYMMDD(value);
    return parsed ? formatToMMDDYYYY(parsed) : '';
  };

  handleChange = (event) => {
    const nativeValue = event.target.value;
    const appValue = this.formatToApp(nativeValue);

    this.setState({ selectedDate: nativeValue });

    if (appValue && appValue !== this.state.lastReportedValue) {
      reportFieldEngagement(this.props.attributeid, appValue);
      this.setState({ lastReportedValue: appValue });
    }

    if (this.props.onupdate) {
      this.props.onupdate(appValue);
    }
  };

  getDateFromType = (dateType, specificDate) => {
    if (!dateType) return null;

    if (
      dateType === 'specific-date-before' ||
      dateType === 'specific-date-after'
    ) {
      return specificDate ? parseISO(specificDate) : null;
    }

    const typeMap = {
      day: new Date(),
      month: endOfMonth(new Date()),
      year: endOfYear(new Date()),
    };

    return typeMap[dateType] || null;
  };

  getDateRange = () => {
    const { validation = {} } = this.props;
    const {
      disallowdatesbefore,
      disallowdatesafter,
      disallowspecificdatesbefore,
      disallowspecificdatesafter,
      rangeLowerBound,
      rangeUpperBound,
    } = validation;

    const today = new Date();
    let minDate = null;
    let maxDate = null;

    const beforeDate = this.getDateFromType(
      disallowdatesbefore,
      disallowspecificdatesbefore
    );
    if (beforeDate) {
      minDate = beforeDate;
    }

    const afterDate = this.getDateFromType(
      disallowdatesafter,
      disallowspecificdatesafter
    );
    if (afterDate) {
      maxDate = afterDate;
    }

    if (rangeLowerBound) {
      const lowerBoundDate = subYears(today, rangeLowerBound);
      if (!maxDate || isBefore(lowerBoundDate, maxDate)) {
        maxDate = lowerBoundDate;
      }
    }

    if (rangeUpperBound) {
      const upperBoundDate = subYears(today, rangeUpperBound);
      if (!minDate || isAfter(upperBoundDate, minDate)) {
        minDate = upperBoundDate;
      }
    }

    return {
      min: minDate ? formatToYYYYMMDD(minDate) : undefined,
      max: maxDate ? formatToYYYYMMDD(maxDate) : undefined,
    };
  };

  render() {
    const { min, max } = this.getDateRange();

    return (
      <div
        className={`ltFormControlText${
          this.props.hasError ? ' ltHasError ' : ' '
        }${this.props.attributeid}`}
      >
        <input
          type="date"
          id={this.props.id}
          aria-label={this.props['aria-label']}
          value={this.state.selectedDate}
          onChange={this.handleChange}
          onBlur={(e) => {
            if (this.props.readOnly) return;
            this.props.onBlur(e);
          }}
          min={min}
          max={max}
          className={this.props.className || 'ltFormControl'}
          style={{ textAlign: 'left' }}
          autoFocus={this.props.autoFocus && window.innerWidth >= 768}
          readOnly={this.props.readOnly}
        />
      </div>
    );
  }
}

NativeDatePicker.propTypes = {
  value: PropTypes.string,
  onupdate: PropTypes.func,
  attributeid: PropTypes.string,
  dispatch: PropTypes.func,
  clearedDefaultedField: PropTypes.func,
  setClearedDefaultedField: PropTypes.func,
  step: PropTypes.func,
  autoFocus: PropTypes.bool,
  validation: PropTypes.object,
};

export default NativeDatePicker;
